import jsQR from 'jsqr';
import { werteScanAus } from '../../shared/qr-format.js';
import { abschnitt, ersetze, h, knopf, liste, meldung, schritte, setzeMeldung, zeile } from '../dom.js';
import { melde } from '../ergebnisse.js';
import { startInfo } from '../geraet.js';
import type { Ansicht } from '../router.js';
import { lies, protokolliere, schreibe } from '../speicher.js';
import { formatiereZahl, groesseText, NB } from '../zeit.js';
import { Aufraeumer, kopf, pruefpunktStand } from './gemeinsam.js';

type Erlaubnis = PermissionState | 'unbekannt';

const ERLAUBNIS_TEXT: Record<Erlaubnis, string> = {
  granted: 'erlaubt',
  prompt: 'wird gefragt',
  denied: 'abgelehnt',
  unbekannt: 'unbekannt (Browser verrät es nicht)',
};

async function erlaubnisZustand(): Promise<Erlaubnis> {
  try {
    const zustand = await navigator.permissions.query({ name: 'camera' as PermissionName });
    return zustand.state;
  } catch {
    return 'unbekannt';
  }
}

function wartenAufBild(video: HTMLVideoElement): Promise<void> {
  return new Promise((fertig, fehler) => {
    if (video.readyState >= 2 && video.videoWidth > 0) return fertig();
    const zeit = window.setTimeout(() => fehler(new Error('Kein Kamerabild nach 8 Sekunden.')), 8_000);
    video.addEventListener(
      'loadeddata',
      () => {
        window.clearTimeout(zeit);
        fertig();
      },
      { once: true },
    );
  });
}

type FaehigkeitenMitLampe = MediaTrackCapabilities & { torch?: boolean };

export const kamera: Ansicht = async (ziel) => {
  const aufraeumer = new Aufraeumer();
  const { startNr } = startInfo();
  const qr = await lies('qr');

  const video = h('video', { class: 'kamerabild', playsinline: true, muted: true, autoplay: true, hidden: true, 'aria-label': 'Kamerabild' });
  video.muted = true;
  const leinwand = document.createElement('canvas');
  const angaben = h('div', {});
  const fotoBereich = h('div', { class: 'foto-vorschau' });
  const statusKamera = meldung();
  const statusLampe = meldung();
  const statusScanner = meldung();
  const lampenInfo = h('p', {}, 'Erst die Kamera starten.');
  const lampenFrage = h('div', { class: 'frage', hidden: true });
  const erlaubnisBereich = h('div', {});

  let strom: MediaStream | undefined;
  let spur: MediaStreamTrack | undefined;
  let lampeAn = false;
  let lampeGemeldet = false;
  let scanTimer: number | undefined;
  let fotoUrl: string | undefined;

  const kameraKnopf = knopf('Kamera starten', () => (strom ? stoppe() : starteKamera()));
  const fotoKnopf = knopf('Foto aufnehmen', () => fotografiere(), 'sekundaer', { disabled: true });
  const lampenKnopf = knopf('Taschenlampe einschalten', () => schalteLampe(), 'sekundaer', { disabled: true, 'aria-pressed': 'false' });
  const scanKnopf = knopf('Scanner starten', () => (scanTimer ? stoppeScan() : starteScan()), 'sekundaer');

  function aktualisiereKnoepfe(): void {
    kameraKnopf.textContent = strom ? 'Kamera stoppen' : 'Kamera starten';
    fotoKnopf.disabled = !strom;
    lampenKnopf.disabled = !strom;
    lampenKnopf.textContent = lampeAn ? 'Taschenlampe ausschalten' : 'Taschenlampe einschalten';
    lampenKnopf.setAttribute('aria-pressed', String(lampeAn));
    scanKnopf.textContent = scanTimer ? 'Scanner stoppen' : 'Scanner starten';
    video.hidden = !strom;
  }

  function stoppe(): void {
    stoppeScan();
    for (const s of strom?.getTracks() ?? []) s.stop();
    strom = undefined;
    spur = undefined;
    lampeAn = false;
    video.srcObject = null;
    aktualisiereKnoepfe();
  }
  aufraeumer.dazu(stoppe);
  aufraeumer.dazu(() => {
    if (fotoUrl) URL.revokeObjectURL(fotoUrl);
  });

  async function starteKamera(): Promise<void> {
    if (!navigator.mediaDevices?.getUserMedia) {
      await melde('kamera', 'nicht_unterstuetzt', 'Der Browser bietet keinen Kamerazugriff.');
      setzeMeldung(statusKamera, 'Kein Kamerazugriff in diesem Browser.', 'fehler');
      return;
    }
    const zustandVorher = await erlaubnisZustand();
    const beginn = performance.now();
    setzeMeldung(statusKamera, 'Kamera wird gestartet …');
    try {
      strom = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      spur = strom.getVideoTracks()[0];
      video.srcObject = strom;
      aktualisiereKnoepfe();
      await video.play().catch(() => {});
      await wartenAufBild(video);
    } catch (fehler) {
      const name = (fehler as DOMException).name;
      const text =
        name === 'NotAllowedError'
          ? 'Kamera-Erlaubnis verweigert. In den Einstellungen des Handys die Kamera für die App bzw. den Browser erlauben und erneut starten.'
          : name === 'NotFoundError'
            ? 'Keine Kamera gefunden.'
            : `Kamera startet nicht: ${name}: ${(fehler as Error).message}`;
      stoppe();
      await melde('kamera', 'fehler', text, { fehler: name, zustandVorher });
      setzeMeldung(statusKamera, text, 'fehler');
      return;
    }
    const dauerMs = Math.round(performance.now() - beginn);
    const einstellungen = spur?.getSettings() ?? {};
    const faehig = (spur?.getCapabilities?.() ?? {}) as FaehigkeitenMitLampe;
    await melde('kamera', 'ok', `Kamera liefert Bild (${video.videoWidth} × ${video.videoHeight}).`, {
      breite: video.videoWidth,
      hoehe: video.videoHeight,
      richtung: einstellungen.facingMode ?? 'unbekannt',
      kamera: spur?.label ?? '',
      dauerMs,
      erlaubnisVorher: zustandVorher,
    });
    await protokolliere('kamera-start', { startNr, zustandVorher, dauerMs });
    setzeMeldung(statusKamera, 'Kamera läuft.', 'ok');
    ersetze(
      angaben,
      liste(
        zeile('Erlaubnis vor dem Start', ERLAUBNIS_TEXT[zustandVorher]),
        zeile('Bild', `${video.videoWidth} × ${video.videoHeight}`),
        zeile('Dauer bis zum Bild', `${formatiereZahl(dauerMs)}${NB}ms`),
        zeile('Kamera', spur?.label || 'unbekannt'),
      ),
    );
    lampenInfo.textContent = faehig.torch
      ? 'Der Browser bietet die Taschenlampe an.'
      : 'Der Browser meldet keine Taschenlampe. Einschalten trotzdem versuchen.';
    aktualisiereKnoepfe();
    await pruefeErlaubnisBleibt(zustandVorher, dauerMs);
  }

  async function pruefeErlaubnisBleibt(zustandVorher: Erlaubnis, dauerMs: number): Promise<void> {
    const ersterStart = await lies('kameraErlaubtInStart');
    if (ersterStart === undefined) {
      await schreibe('kameraErlaubtInStart', startNr);
      await melde('kamera-erlaubnis', 'offen', 'Kamera einmal erlaubt. Jetzt die App ganz schließen, neu öffnen und die Kamera erneut starten.', {
        ersteErlaubnisStart: startNr,
      });
      ersetze(erlaubnisBereich, h('p', { class: 'meldung meldung-info' }, 'Nächster Schritt: App ganz schließen, neu öffnen und hier die Kamera erneut starten.'));
      return;
    }
    if (startNr <= ersterStart) {
      ersetze(erlaubnisBereich, h('p', { class: 'meldung meldung-info' }, 'Die App wurde seit der ersten Erlaubnis nicht neu gestartet. Erst App ganz schließen und neu öffnen.'));
      return;
    }
    const details = { erlaubnisVorher: zustandVorher, startNr, ersteErlaubnisStart: ersterStart, dauerMs };
    const antworten = async (gefragt: boolean) => {
      await melde(
        'kamera-erlaubnis',
        gefragt ? 'fehler' : 'ok',
        gefragt ? 'Nach dem Neustart der App fragte das Handy erneut nach der Kamera.' : 'Kamera-Erlaubnis blieb nach dem Neustart der App erhalten.',
        { ...details, erneutGefragt: gefragt },
      );
      ersetze(erlaubnisBereich, h('p', { class: 'meldung meldung-ok' }, 'Antwort gespeichert.'));
    };
    ersetze(
      erlaubnisBereich,
      h(
        'div',
        { class: 'frage', role: 'group', 'aria-labelledby': 'frage-erlaubnis' },
        h('p', { id: 'frage-erlaubnis' }, `Hat das Handy beim Start der Kamera gerade erneut um Erlaubnis gefragt? (Browser meldete vorher: ${ERLAUBNIS_TEXT[zustandVorher]})`),
        knopf('Nein, keine Frage', () => antworten(false)),
        knopf('Ja, erneut gefragt', () => antworten(true), 'sekundaer'),
      ),
    );
  }

  async function fotografiere(): Promise<void> {
    if (!strom) return;
    leinwand.width = video.videoWidth;
    leinwand.height = video.videoHeight;
    leinwand.getContext('2d')?.drawImage(video, 0, 0);
    const bild = await new Promise<Blob | null>((fertig) => leinwand.toBlob(fertig, 'image/jpeg', 0.85));
    if (!bild) {
      setzeMeldung(statusKamera, 'Foto konnte nicht erzeugt werden.', 'fehler');
      return;
    }
    if (fotoUrl) URL.revokeObjectURL(fotoUrl);
    fotoUrl = URL.createObjectURL(bild);
    ersetze(fotoBereich, h('img', { src: fotoUrl, alt: 'Aufgenommenes Testfoto', width: 160, height: Math.round((160 * leinwand.height) / leinwand.width) }), h('p', {}, `Foto: ${leinwand.width} × ${leinwand.height}, ${groesseText(bild.size)}`));
  }

  async function schalteLampe(): Promise<void> {
    if (!spur) return;
    const faehig = (spur.getCapabilities?.() ?? {}) as FaehigkeitenMitLampe;
    const ziel = !lampeAn;
    try {
      await spur.applyConstraints({ advanced: [{ torch: ziel } as MediaTrackConstraintSet] });
      lampeAn = ziel;
      aktualisiereKnoepfe();
      if (ziel && !lampeGemeldet) {
        lampenFrage.hidden = false;
        lampenFrage.querySelector('button')?.focus();
      }
    } catch (fehler) {
      const text = faehig.torch
        ? `Taschenlampe lässt sich nicht einschalten: ${(fehler as Error).message}`
        : 'Der Browser bietet die Taschenlampe nicht an.';
      await melde('taschenlampe', faehig.torch ? 'fehler' : 'nicht_unterstuetzt', text, { browserMeldetLampe: faehig.torch === true });
      setzeMeldung(statusLampe, text, 'fehler');
    }
  }

  const lampenAntwort = async (leuchtet: boolean) => {
    const faehig = (spur?.getCapabilities?.() ?? {}) as FaehigkeitenMitLampe;
    await melde('taschenlampe', leuchtet ? 'ok' : 'fehler', leuchtet ? 'Taschenlampe leuchtet.' : 'Eingeschaltet, aber die Lampe leuchtet nicht.', {
      browserMeldetLampe: faehig.torch === true,
    });
    lampeGemeldet = true;
    lampenFrage.hidden = true;
    setzeMeldung(statusLampe, 'Antwort gespeichert.', 'ok');
  };
  lampenFrage.append(h('p', {}, 'Leuchtet die Taschenlampe?'), knopf('Ja, sie leuchtet', () => lampenAntwort(true)), knopf('Nein', () => lampenAntwort(false), 'sekundaer'));

  async function starteScan(): Promise<void> {
    if (!strom) await starteKamera();
    if (!strom) return;
    const beginn = performance.now();
    const breite = Math.min(800, video.videoWidth);
    const hoehe = Math.round((video.videoHeight / video.videoWidth) * breite);
    const scanLeinwand = document.createElement('canvas');
    scanLeinwand.width = breite;
    scanLeinwand.height = hoehe;
    const kontext = scanLeinwand.getContext('2d', { willReadFrequently: true });
    setzeMeldung(statusScanner, 'Scanner läuft: Aufkleber vor die Kamera halten.');
    const runde = async () => {
      if (!kontext || !strom) return;
      kontext.drawImage(video, 0, 0, breite, hoehe);
      const code = jsQR(kontext.getImageData(0, 0, breite, hoehe).data, breite, hoehe, { inversionAttempts: 'dontInvert' });
      if (code) {
        const ergebnis = werteScanAus(code.data, qr ? [qr.host] : []);
        if (ergebnis.art === 'tfm') {
          const sekunden = Math.round((performance.now() - beginn) / 100) / 10;
          stoppeScan();
          navigator.vibrate?.(80);
          await melde('scanner', 'ok', `Aufkleber ${ergebnis.kuerzel}/${ergebnis.schluessel} erkannt nach ${formatiereZahl(sekunden)}${NB}s.`, {
            dauerMs: Math.round(performance.now() - beginn),
            taschenlampe: lampeAn,
            bild: `${video.videoWidth} × ${video.videoHeight}`,
          });
          setzeMeldung(statusScanner, `Erkannt: ${ergebnis.kuerzel}/${ergebnis.schluessel}`, 'ok');
          return;
        }
        setzeMeldung(statusScanner, ergebnis.art === 'fremd' ? 'Dieser Aufkleber stammt nicht von TFM.' : `Kein Link: ${code.data.slice(0, 60)}`, 'fehler');
      }
      scanTimer = window.setTimeout(() => void runde(), 150);
    };
    scanTimer = window.setTimeout(() => void runde(), 0);
    aktualisiereKnoepfe();
  }

  function stoppeScan(): void {
    window.clearTimeout(scanTimer);
    scanTimer = undefined;
    aktualisiereKnoepfe();
  }

  ziel.append(
    ...kopf('Kamera und Taschenlampe', 'Rückkamera, Taschenlampe und Scanner in der App; am iPhone außerdem, ob die Kamera-Erlaubnis nach einem Neustart der App bleibt.'),
    abschnitt('Kamera', pruefpunktStand('kamera', aufraeumer), video, h('div', { class: 'knopfreihe' }, kameraKnopf, fotoKnopf), statusKamera, angaben, fotoBereich),
    abschnitt('Taschenlampe', pruefpunktStand('taschenlampe', aufraeumer), lampenInfo, lampenKnopf, lampenFrage, statusLampe),
    abschnitt(
      'Scanner in der App',
      pruefpunktStand('scanner', aufraeumer),
      h('p', {}, 'Einen Test-Aufkleber (Seite „QR-Aufkleber drucken“) vor die Kamera halten, am besten auch einmal im Dunkeln mit Taschenlampe.'),
      scanKnopf,
      statusScanner,
    ),
    abschnitt(
      'Kamera-Erlaubnis bleibt',
      pruefpunktStand('kamera-erlaubnis', aufraeumer),
      schritte('Oben die Kamera starten und erlauben.', 'App ganz schließen: App-Umschalter öffnen und die App nach oben wegwischen.', 'App wieder öffnen und hier die Kamera erneut starten.', 'Angeben, ob das Handy erneut gefragt hat.'),
      erlaubnisBereich,
    ),
  );
  aktualisiereKnoepfe();
  return aufraeumer.alles;
};
